
import { useCallback, useMemo } from 'react';
import { getResearchTypeById } from '@/types/research-types';
import { formatDistanceToNow } from 'date-fns';
import { getFilterDisplayData } from '../research-matrix-utils/filter-display-utils';
import { stripMarkdownAndTruncate } from '../research-matrix-utils/content-utils';

export const useResearchCardData = (research: any) => {
  return useMemo(() => {
    const formattedDate = formatDistanceToNow(new Date(research.created_at), { addSuffix: true });
    
    // Get favorite status and count from the research data
    const isFavorited = research.is_favorited || false;
    const favoriteCount = research.favorite_count || 0;
    
    // Use the centralized research type system with legacy fallback
    const researchTypeConfig = getResearchTypeById(research.research_type) || getResearchTypeById('Business & Professional');
    
    // Use the stripMarkdownAndTruncate utility to clean the content
    const contentSnippet = stripMarkdownAndTruncate(research.content, 150);
    
    const filterPills = getFilterDisplayData(research);
    
    const locationDisplay = research.research_parameters?.country && research.research_parameters.country !== 'global' 
      ? research.research_parameters.country 
      : null;
      
    // Use source_type instead of source_quality
    const sourceTypeDisplay = research.research_parameters?.source_type && research.research_parameters.source_type !== 'general' 
      ? research.research_parameters.source_type 
      : null;

    // Extract timeframe with proper formatting
    const timeframeDisplay = research.research_parameters?.timeframe_label || 
      (research.research_parameters?.timeframe ? 
        typeof research.research_parameters.timeframe === 'string' ? 
          research.research_parameters.timeframe.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
          : null
        : null);

    // Extract type-specific filters for display
    const typeSpecificFilters = research.research_parameters?.type_specific_filters || {};
    const typeSpecificTags = Object.values(typeSpecificFilters)
      .filter(value => value && typeof value === 'string' && value.trim() !== '')
      .map(value => typeof value === 'string' ? value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : value)
      .slice(0, 3); // Limit to 3 most important tags

    return {
      formattedDate,
      isFavorited,
      favoriteCount,
      researchTypeConfig,
      contentSnippet,
      filterPills,
      locationDisplay,
      sourceTypeDisplay,
      timeframeDisplay,
      typeSpecificTags,
    };
  }, [research]);
};
